// Phase 6 — Posture (v2-native).
//
// Same visual primitives as `_new/detection/WAFEvent.js`:
//   * tableHead / tableBody typography (size 14, weight 500 / 400)
//   * severity pill (filled background, dark text) via asecSeverityConfig
//   * status dot (12px circle) for open / resolved / suppressed
//   * mono-blue treatment on host + path + finding-kind
//   * skeleton rows while loading
//   * hoverable, cursor:pointer rows
// Tabs (Findings / Risk scores) stay because operators expect them.
import React from "react";
import {
  Stack, Box, Typography, Button, Tabs, Tab, Select, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, Skeleton, Chip,
  TablePagination, IconButton,
  Dialog, DialogContent, DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { EyeOff } from "lucide-react";
import asec from "../../../../../utils/axios/v1/asecAxios";
import colors from "../../../../../utils/colors";
import { formatDate } from "../../../../../utils/format";
import AsecPage from "./AsecPage";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { SafeTextField } from "../../../../common/Greens";
import LayerdIcon from "../../../../common/LayerdIcon";
import RefreshIconButton from "../../../../common/RefreshIconButton";
import {
  asecBtnOutlinedSm, asecBtnPrimarySm,
  asecTabsSx, filterSelectSx,
  asecEventTableHeadSx, asecEventTableBodySx, asecMonoBlueSx, asecEventRowSx,
  asecFindingsTableSx, asecSeverityConfig, asecStatusDotColor,
  asecDialogActionsSx, asecIconButtonSx, asecFieldLabelSx,
} from "./asecUi";
import {
  AsecEventsHeadCell,
  AsecEventsActivityCell,
  AsecEventsHostnameCell,
  AsecEventsViewCell,
} from "./components/AsecEventsTable";
import { AsecEventsFilterBar } from "./components/AsecEventsFilterBar";
import PostureFindingDrawer from "./components/PostureFindingDrawer";

const ROWS_PER_PAGE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 25;

const tablePaginationSx = {
  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
    fontSize: 14, color: colors.neutral[700],
  },
};

const FINDING_STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "open", label: "Open only" },
  { value: "suppressed", label: "Suppressed" },
  { value: "resolved", label: "Resolved" },
];

const SEVERITY_FILTERS = [
  { value: "", label: "All severities" },
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "info", label: "Info" },
];

function paginateRows(rows, page, pageSize) {
  const start = page * pageSize;
  return rows.slice(start, start + pageSize);
}

function matchesSeverityFilter(rowSeverity, filter) {
  if (!filter) return true;
  return String(rowSeverity || "").toLowerCase() === String(filter).toLowerCase();
}

function filterRowsBySeverity(rows, severityFilter) {
  if (!severityFilter || !Array.isArray(rows)) return rows;
  return rows.filter((row) => matchesSeverityFilter(row.severity, severityFilter));
}

function getFindingsEmptyMessage(statusFilter, severityFilter) {
  if (severityFilter) return "No posture findings match the current severity filter.";
  switch (statusFilter) {
    case "resolved":
      return "No resolved posture findings for the current filters.";
    case "suppressed":
      return "No suppressed posture findings for the current filters.";
    case "all":
      return "No posture findings recorded yet.";
    default:
      return "No open posture findings for the current filters.";
  }
}

function getRiskScoresEmptyMessage(severityFilter) {
  if (severityFilter) return "No risk scores match the current severity filter.";
  return "No risk scores yet. The posture scanner runs on a 24-hour cadence; check back after the next tick.";
}

function formatFindingActionMessage(body) {
  if (body?.action === "resolve") return "Posture finding resolved.";
  if (body?.action === "suppress") return "Posture finding suppressed for 7 days.";
  return "Posture finding updated.";
}

function SuppressReasonDialog({ open, onClose, onConfirm, saving }) {
  const [reason, setReason] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setReason("");
      setError("");
    }
  }, [open]);

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("Enter a reason — suppression requires one and lasts 7 days.");
      return;
    }
    const ok = await onConfirm(trimmed);
    if (ok) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(17, 17, 17, 0.45)" } },
      }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 16px 48px rgba(17, 17, 17, 0.14)",
          overflow: "hidden",
          backgroundColor: "#fff",
        },
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 0, backgroundColor: "#fff" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                bgcolor: colors.primary[100],
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <LayerdIcon icon={EyeOff} size="md" color="primary" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "Rubik",
                  fontSize: 18,
                  fontWeight: 600,
                  lineHeight: "26px",
                  color: colors.neutral[1000],
                }}
              >
                Suppress posture finding
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Rubik",
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: colors.neutral[600],
                  mt: 0.5,
                }}
              >
                This finding will be hidden from the open list for 7 days. Add a short reason so your team knows why it was suppressed.
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            disabled={saving}
            sx={{ ...asecIconButtonSx, mt: -0.5, mr: -0.5 }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>
      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1, backgroundColor: "#fff" }}>
        <Typography component="label" sx={{ ...asecFieldLabelSx, mb: 1 }}>
          Reason
        </Typography>
        <SafeTextField
          autoFocus
          multiline
          minRows={3}
          fullWidth
          placeholder="e.g. Known false positive on staging endpoint"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError("");
          }}
          error={Boolean(error)}
          helperText={error || "Required"}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              fontSize: 14,
              fontFamily: "Rubik",
              backgroundColor: "#fff",
              "& fieldset": { borderColor: colors.neutral[200] },
              "&:hover fieldset": { borderColor: colors.neutral[300] },
              "&.Mui-focused fieldset": { borderColor: colors.primary[300] },
            },
            "& .MuiFormHelperText-root": {
              fontFamily: "Rubik",
              fontSize: 12,
              color: error ? undefined : colors.neutral[600],
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ ...asecDialogActionsSx, justifyContent: "flex-end", backgroundColor: "#fff" }}>
        <Button onClick={onClose} disabled={saving} sx={asecBtnOutlinedSm}>Cancel</Button>
        <Button onClick={handleConfirm} disabled={saving} sx={asecBtnPrimarySm}>
          {saving ? "Suppressing…" : "Suppress for 7 days"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SkeletonRows({ rows = ROWS_PER_PAGE, cols }) {
  return (
    <TableBody>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={`sk_${r}`}>
          {Array.from({ length: cols }).map((__, c) => (
            <TableCell key={`sk_${r}_${c}`} sx={{ py: 2 }}>
              <Skeleton variant="rectangular" height={22} sx={{ borderRadius: "11px" }} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

function SeverityPill({ severity }) {
  const c = asecSeverityConfig(severity);
  return (
    <Chip
      size="small"
      label={c.label}
      sx={{
        px: 1, height: 22,
        background: c.bgColor, color: c.color,
        borderRadius: "30px",
        fontSize: 12, fontWeight: 500,
      }}
    />
  );
}

function StatusCell({ resolvedAt, suppressedUntil }) {
  const state = resolvedAt ? "resolved" : suppressedUntil ? "suppressed" : "open";
  const label = state[0].toUpperCase() + state.slice(1);
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: asecStatusDotColor(state) }} />
      <Typography sx={asecEventTableBodySx}>{label}</Typography>
    </Stack>
  );
}

function FindingsTable({ findings, statusFilter, severityFilter, onOpen }) {
  const cols = ["Hostname", "Activity", "Finding", "Severity", "Status", "Detected", "Action"];
  const headRow = (
    <TableHead><TableRow>
      {cols.map((h, i) => (
        <AsecEventsHeadCell key={h || "view"} align={i === cols.length - 1 ? "right" : "left"}>
          {h}
        </AsecEventsHeadCell>
      ))}
    </TableRow></TableHead>
  );
  if (findings === null) {
    return (
      <Table sx={asecFindingsTableSx}>
        {headRow}
        <SkeletonRows cols={cols.length} />
      </Table>
    );
  }
  if (findings.length === 0) {
    return (
      <Table sx={asecFindingsTableSx}>
        {headRow}
        <TableBody><TableRow>
          <TableCell colSpan={cols.length}>
            <Typography textAlign="center" sx={{ ...asecEventTableBodySx, py: 4, color: colors.neutral[500] }}>
              {getFindingsEmptyMessage(statusFilter, severityFilter)}
            </Typography>
          </TableCell>
        </TableRow></TableBody>
      </Table>
    );
  }
  return (
    <Table sx={asecFindingsTableSx}>
      {headRow}
      <TableBody>
        {findings.map((r) => (
          <TableRow
            key={r.id}
            onClick={() => onOpen?.(r)}
            sx={asecEventRowSx}
          >
            <TableCell>
              <AsecEventsHostnameCell host={r.host} siteId={r.site_id} />
            </TableCell>
            <TableCell>
              <AsecEventsActivityCell method={r.method} path={r.path_template} />
            </TableCell>
            <TableCell>
              <Stack>
                <Typography sx={asecEventTableBodySx}>{prettifyKind(r.finding_kind)}</Typography>
                <Typography sx={asecMonoBlueSx}>{r.finding_kind}</Typography>
              </Stack>
            </TableCell>
            <TableCell><SeverityPill severity={r.severity} /></TableCell>
            <TableCell>
              <StatusCell resolvedAt={r.resolved_at} suppressedUntil={r.suppressed_until} />
            </TableCell>
            <TableCell>
              <Typography sx={asecEventTableBodySx}>
                {r.detected_at ? formatDate(r.detected_at) : "—"}
              </Typography>
            </TableCell>
            <AsecEventsViewCell onClick={() => onOpen?.(r)} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RiskScoresTable({ scores, allScores, emptyMessage }) {
  const cols = ["Endpoint", "Score", "Severity", "Components", "Updated"];
  if (allScores === null) {
    return (
      <Table size="small">
        <TableHead><TableRow>
          {cols.map((h) => <TableCell key={h} sx={asecEventTableHeadSx}>{h}</TableCell>)}
        </TableRow></TableHead>
        <SkeletonRows cols={cols.length} />
      </Table>
    );
  }
  if (allScores.length === 0) {
    return (
      <Table size="small">
        <TableHead><TableRow>
          {cols.map((h) => <TableCell key={h} sx={asecEventTableHeadSx}>{h}</TableCell>)}
        </TableRow></TableHead>
        <TableBody><TableRow>
          <TableCell colSpan={cols.length}>
            <Typography textAlign="center" sx={{ ...asecEventTableBodySx, py: 4, color: colors.neutral[500] }}>
              {emptyMessage}
            </Typography>
          </TableCell>
        </TableRow></TableBody>
      </Table>
    );
  }
  return (
    <Table size="small">
      <TableHead><TableRow>
        {cols.map((h) => <TableCell key={h} sx={asecEventTableHeadSx}>{h}</TableCell>)}
      </TableRow></TableHead>
      <TableBody>
        {(scores || []).map((r) => {
          const ep = formatEndpoint(r);
          return (
            <TableRow key={r.endpoint_id} sx={asecEventRowSx}>
              <TableCell>
                <Stack>
                  <Typography sx={asecEventTableBodySx}>{ep.host}</Typography>
                  <Typography sx={asecMonoBlueSx}>{ep.method} {ep.path}</Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Typography sx={{ ...asecEventTableBodySx, fontWeight: 600 }}>{r.score}</Typography>
              </TableCell>
              <TableCell><SeverityPill severity={r.severity} /></TableCell>
              <TableCell>
                <ComponentsSummary components={r.components} />
              </TableCell>
              <TableCell>
                <Typography sx={asecEventTableBodySx}>
                  {r.computed_at ? formatDate(r.computed_at) : "—"}
                </Typography>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function ComponentsSummary({ components }) {
  if (!components || typeof components !== "object") {
    return <Typography sx={{ ...asecEventTableBodySx, color: colors.neutral[500] }}>—</Typography>;
  }
  const bits = [];
  if (typeof components.posture_base === "number") bits.push(`posture ${components.posture_base}`);
  if (components.unauth_penalty > 0)              bits.push(`unauth +${components.unauth_penalty}`);
  if (components.spec_penalty > 0)                bits.push(`spec +${components.spec_penalty}`);
  if (components.dlp_penalty > 0)                 bits.push(`dlp +${components.dlp_penalty}`);
  if (typeof components.sensitivity_multiplier === "number" && components.sensitivity_multiplier !== 1) {
    bits.push(`× ${components.sensitivity_multiplier.toFixed(1)}`);
  }
  return (
    <Typography sx={{ ...asecEventTableBodySx, fontSize: 12, color: colors.neutral[700] }}>
      {bits.length > 0 ? bits.join(" · ") : "—"}
    </Typography>
  );
}

function prettifyKind(kind) {
  if (!kind) return "—";
  const dot = kind.indexOf(".");
  if (dot < 0) return kind;
  const family = kind.slice(0, dot).toUpperCase();
  const tail = kind.slice(dot + 1).replace(/_/g, " ");
  return `${family} · ${tail[0].toUpperCase() + tail.slice(1)}`;
}

function formatEndpoint(r) {
  return {
    host: r.host || r.site_id || "—",
    method: r.method || "GET",
    path: r.path_template || "—",
  };
}

function Body({ siteId }) {
  const { showSuccess, showError } = useNotification();
  const [sub, setSub] = React.useState(0);
  const [findings, setFindings] = React.useState(null);
  const [scores, setScores] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [filterSev, setFilterSev] = React.useState("");
  const [findingsPage, setFindingsPage] = React.useState(0);
  const [findingsPageSize, setFindingsPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [findingsTotal, setFindingsTotal] = React.useState(0);
  const [scoresPage, setScoresPage] = React.useState(0);
  const [scoresPageSize, setScoresPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [scoresTotal, setScoresTotal] = React.useState(0);
  const [scoresSeverity, setScoresSeverity] = React.useState("");
  const [drawerFinding, setDrawerFinding] = React.useState(null);
  const [suppressFindingId, setSuppressFindingId] = React.useState(null);
  const [suppressSaving, setSuppressSaving] = React.useState(false);
  const [actionSaving, setActionSaving] = React.useState(false);
  const findingsLoadSeq = React.useRef(0);
  const scoresLoadSeq = React.useRef(0);

  const reloadFindings = React.useCallback(async () => {
    if (!siteId) return;
    const seq = ++findingsLoadSeq.current;
    setFindings(null);
    const params = {
      page: findingsPage,
      limit: findingsPageSize,
      status: statusFilter,
    };
    if (filterSev) params.severity = filterSev;
    try {
      const f = await asec.postureFindings(siteId, params);
      if (seq !== findingsLoadSeq.current) return;
      const items = Array.isArray(f.data?.items) ? f.data.items : Array.isArray(f.data) ? f.data : [];
      setFindings(filterRowsBySeverity(items, filterSev));
      setFindingsTotal(typeof f.data?.total === "number" ? f.data.total : items.length);
    } catch (e) {
      if (seq !== findingsLoadSeq.current) return;
      setFindings([]);
      setFindingsTotal(0);
      showError(e?.response?.data?.message || e?.message || String(e));
    }
  }, [siteId, statusFilter, filterSev, findingsPage, findingsPageSize, showError]);

  const reloadScores = React.useCallback(async () => {
    if (!siteId) return;
    const seq = ++scoresLoadSeq.current;
    setScores(null);
    const params = {
      page: scoresPage,
      limit: scoresPageSize,
    };
    if (scoresSeverity) params.severity = scoresSeverity;
    try {
      const r = await asec.postureRiskScores(siteId, params);
      if (seq !== scoresLoadSeq.current) return;
      const payload = r.data;
      const items = Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload)
        ? payload
        : [];
      setScores(items);
      setScoresTotal(typeof payload?.total === "number" ? payload.total : items.length);
    } catch (e) {
      if (seq !== scoresLoadSeq.current) return;
      setScores([]);
      setScoresTotal(0);
      showError(e?.response?.data?.message || e?.message || String(e));
    }
  }, [siteId, scoresSeverity, scoresPage, scoresPageSize, showError]);

  React.useEffect(() => { reloadFindings(); }, [reloadFindings]);
  React.useEffect(() => { reloadScores(); }, [reloadScores]);

  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil((scoresTotal || 0) / scoresPageSize) - 1);
    if (scoresPage > maxPage) setScoresPage(maxPage);
  }, [scoresTotal, scoresPage, scoresPageSize]);

  const findingAction = async (id, body) => {
    try {
      await asec.postureFindingPatch(siteId, id, body);
      showSuccess(formatFindingActionMessage(body));
      reloadFindings();
      return true;
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || String(e);
      showError(msg);
      return false;
    }
  };

  const resolveFinding = async (id) => {
    setActionSaving(true);
    try {
      return await findingAction(id, { action: "resolve" });
    } finally {
      setActionSaving(false);
    }
  };

  const confirmSuppress = async (reason) => {
    if (!suppressFindingId) return false;
    setSuppressSaving(true);
    try {
      const ok = await findingAction(suppressFindingId, { action: "suppress", reason });
      if (ok) setDrawerFinding(null);
      return ok;
    } finally {
      setSuppressSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Tabs value={sub} onChange={(_, v) => setSub(v)} sx={asecTabsSx}>
        <Tab label={`Findings${findings !== null ? ` (${findingsTotal})` : ""}`} />
        <Tab label={`Risk scores${scores !== null ? ` (${scoresTotal})` : ""}`} />
      </Tabs>

      {sub === 0 && (
        <Stack spacing={2}>
          <AsecEventsFilterBar
            trailing={(
              <RefreshIconButton
                onClick={reloadFindings}
                disabled={findings === null}
                loading={findings === null}
                size="compact"
              />
            )}
          >
            <Select
              value={statusFilter}
              onChange={(e) => {
                setFindingsPage(0);
                setStatusFilter(e.target.value);
              }}
              sx={{ ...filterSelectSx, minWidth: 180 }}
            >
              {FINDING_STATUS_FILTERS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
            <Select
              value={filterSev}
              onChange={(e) => {
                setFindingsPage(0);
                setFilterSev(e.target.value);
              }}
              displayEmpty
              sx={{ ...filterSelectSx, minWidth: 160 }}
            >
              {SEVERITY_FILTERS.map((opt) => (
                <MenuItem key={opt.value || "all"} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </AsecEventsFilterBar>
          <Box sx={{ borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`, overflow: "hidden" }}>
            <FindingsTable
              findings={findings}
              statusFilter={statusFilter}
              severityFilter={filterSev}
              onOpen={setDrawerFinding}
            />
            <PostureFindingDrawer
              open={Boolean(drawerFinding)}
              onClose={() => setDrawerFinding(null)}
              row={drawerFinding}
              onResolve={resolveFinding}
              onSuppressRequest={setSuppressFindingId}
              actionSaving={actionSaving || suppressSaving}
            />
            <SuppressReasonDialog
              open={Boolean(suppressFindingId)}
              onClose={() => setSuppressFindingId(null)}
              onConfirm={confirmSuppress}
              saving={suppressSaving}
            />
            {findings !== null && (
              <TablePagination
                component="div"
                count={findingsTotal}
                page={findingsPage}
                onPageChange={(_, p) => setFindingsPage(p)}
                rowsPerPage={findingsPageSize}
                onRowsPerPageChange={(e) => {
                  setFindingsPageSize(parseInt(e.target.value, 10));
                  setFindingsPage(0);
                }}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                sx={tablePaginationSx}
              />
            )}
          </Box>
        </Stack>
      )}

      {sub === 1 && (
        <Stack spacing={2}>
          <AsecEventsFilterBar
            trailing={(
              <RefreshIconButton
                onClick={reloadScores}
                disabled={scores === null}
                loading={scores === null}
                size="compact"
              />
            )}
          >
            <Select
              value={scoresSeverity}
              onChange={(e) => {
                setScoresPage(0);
                setScoresSeverity(e.target.value);
              }}
              displayEmpty
              sx={{ ...filterSelectSx, minWidth: 160 }}
            >
              {SEVERITY_FILTERS.map((opt) => (
                <MenuItem key={opt.value || "all"} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </AsecEventsFilterBar>
          <Box sx={{ borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`, overflow: "hidden" }}>
            <RiskScoresTable
              scores={scores}
              allScores={scores}
              emptyMessage={getRiskScoresEmptyMessage(scoresSeverity)}
            />
            {scores !== null && (
              <TablePagination
                component="div"
                count={scoresTotal}
                page={scoresPage}
                onPageChange={(_, p) => setScoresPage(p)}
                rowsPerPage={scoresPageSize}
                onRowsPerPageChange={(e) => {
                  setScoresPageSize(parseInt(e.target.value, 10));
                  setScoresPage(0);
                }}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                sx={tablePaginationSx}
              />
            )}
          </Box>
        </Stack>
      )}
    </Stack>
  );
}

export default function AsecPosture() {
  return <AsecPage featureKey="api.posture_scanning" helpTooltip="TLS / headers / CORS / debug-endpoint findings + per-endpoint risk scores aggregated 0-100. Suppress to silence a known-OK finding; Resolve when you have fixed it." title="Posture">{(siteId) => <Body siteId={siteId} />}</AsecPage>;
}
